"use client";

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  X,
  FileText,
  Building2,
  Boxes,
  Tag,
  Tags,
  Coins,
  CalendarDays,
  Handshake,
  Plus,
  Check,
  Save,
} from "lucide-react";

interface InvestmentDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpjMatriz?: string;
  unidadeNegocios?: string;
  natureza?: string;
  tipo?: string;
  subTipo?: string;
  naturezaValor?: string;
  vigenciaDe?: string;
  vigenciaAte?: string;
  demandaAcordada?: string;
  prazo?: string;
  demaisContrapartida?: string;
  onSave?: (data: SavedInvestmentDetail) => void;
}

export interface SavedInvestmentDetail {
  cnpjMatriz: string;
  unidadeNegocios: string;
  tipo: string;
  subTipo: string;
  natureza: string;
  naturezaValor: string;
  vigenciaDe: string;
  vigenciaAte: string;
  demandaAcordada: string;
  prazo: string;
  demaisContrapartida: string;
}

function Row({
  label,
  children,
  required = false,
  icon: Icon,
}: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        {Icon && <Icon className="h-4 w-4 text-tudo-blue/70" />}
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      <div className="flex min-h-9 flex-1 items-center gap-1">
        {children}
      </div>
    </div>
  );
}

function SectionHeader({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="mb-3 flex items-center gap-2 border-b border-border pb-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-tudo-blue/10">
        <Icon className="h-4 w-4 text-tudo-blue" />
      </div>
      <h3 className="text-sm font-bold uppercase tracking-wide text-tudo-blue">
        {children}
      </h3>
    </div>
  );
}

function ClearButton({ onClick, visible }: { onClick: () => void; visible: boolean }) {
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
      aria-label="Limpar"
    >
      <X className="h-4 w-4" />
    </button>
  );
}

function ClearableSelect({
  value,
  onChange,
  placeholder,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-full border-border text-sm focus:ring-tudo-blue data-[placeholder]:text-muted-foreground">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
      <ClearButton onClick={() => onChange("")} visible={!!value} />
    </>
  );
}

function ClearableInput({
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  className?: string;
}) {
  return (
    <>
      <Input
        type={type}
        inputMode={inputMode}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`h-9 border-border text-sm focus-visible:ring-tudo-blue ${className ?? ""}`}
      />
      <ClearButton onClick={() => onChange("")} visible={!!value} />
    </>
  );
}

export function InvestmentDetailModal({
  open,
  onOpenChange,
  cnpjMatriz = "19.105.575/0001-05",
  unidadeNegocios = "—",
  natureza: naturezaProp = "",
  tipo: tipoProp = "",
  subTipo: subTipoProp = "",
  naturezaValor: naturezaValorProp = "",
  vigenciaDe: vigenciaDeProp = "",
  vigenciaAte: vigenciaAteProp = "",
  demandaAcordada: demandaAcordadaProp = "",
  prazo: prazoProp = "",
  demaisContrapartida: demaisContrapartidaProp = "",
  onSave,
}: InvestmentDetailModalProps) {
  const [tipo, setTipo] = React.useState(tipoProp);
  const [subTipo, setSubTipo] = React.useState(subTipoProp);
  const [natureza, setNatureza] = React.useState(naturezaProp);
  const [naturezaValor, setNaturezaValor] = React.useState(naturezaValorProp);
  const [vigenciaDe, setVigenciaDe] = React.useState(vigenciaDeProp);
  const [vigenciaAte, setVigenciaAte] = React.useState(vigenciaAteProp);
  const [demandaAcordada, setDemandaAcordada] = React.useState(demandaAcordadaProp);
  const [prazo, setPrazo] = React.useState(prazoProp);
  const [demaisOpen, setDemaisOpen] = React.useState(false);
  const [demaisContrapartida, setDemaisContrapartida] = React.useState(demaisContrapartidaProp);
  const guardCloseRef = React.useRef(false);

  const handleDemaisOpenChange = (next: boolean) => {
    if (!next) {
      // Bloqueia o fechamento acidental do modal de detalhe enquanto
      // o foco retorna do submodal "Demais Contrapartidas".
      guardCloseRef.current = true;
      window.setTimeout(() => {
        guardCloseRef.current = false;
      }, 350);
    }
    setDemaisOpen(next);
  };

  React.useEffect(() => {
    if (open) {
      setNatureza(naturezaProp);
      setTipo(tipoProp);
      setSubTipo(subTipoProp);
      setNaturezaValor(naturezaValorProp);
      setVigenciaDe(vigenciaDeProp);
      setVigenciaAte(vigenciaAteProp);
      setDemandaAcordada(demandaAcordadaProp);
      setPrazo(prazoProp);
      setDemaisContrapartida(demaisContrapartidaProp);
    }
  }, [open, naturezaProp, tipoProp, subTipoProp, naturezaValorProp, vigenciaDeProp, vigenciaAteProp, demandaAcordadaProp, prazoProp, demaisContrapartidaProp]);

  const tipoOptions = [
    "Ação de relacionamento",
    "Ação promocional",
    "Bonificação",
    "Compra de espaço",
    "Investimento promocional",
    "Material promocional",
    "Treinamento e capacitação",
  ];

  const subTipoOptions = [
    "Brinde",
    "TV mídia",
    "Campanha de vendas",
    "Campanha mega venda Novalgina",
    "Campanha de vendas Allegra",
    "Campanha de vendas Losartana",
  ];

  const handleSave = () => {
    onSave?.({
      cnpjMatriz,
      unidadeNegocios,
      tipo,
      subTipo,
      natureza,
      naturezaValor,
      vigenciaDe,
      vigenciaAte,
      demandaAcordada,
      prazo,
      demaisContrapartida,
    });
    onOpenChange(false);
  };

  const handleNaturezaChange = (val: string) => {
    setNatureza(val);
    setNaturezaValor("");
  };

  const handleNaturezaInputChange = (val: string) => {
    if (natureza === "Valor") {
      const raw = val.replace(/\D/g, "");
      const num = Number(raw) / 100;
      setNaturezaValor(
        num.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        })
      );
    } else {
      setNaturezaValor(val);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => {
          if (demaisOpen || guardCloseRef.current) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (demaisOpen || guardCloseRef.current) e.preventDefault();
        }}
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border-none bg-muted/30 p-0 shadow-2xl lg:max-w-4xl [&>button]:hidden"
      >
        {/* Header com gradiente */}
        <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-6 py-4">
          <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-left text-lg font-bold tracking-wide text-white">
                  Detalhe do investimento
                </DialogTitle>
                <p className="text-xs font-medium text-white/70">
                  Configure os parâmetros e contrapartidas
                </p>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          {/* Detalhe do Investimento */}
          <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <SectionHeader icon={FileText}>Detalhe do Investimento</SectionHeader>

            <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
              <Row label="CNPJs Matriz:" icon={Building2}>
                <span className="text-sm font-medium text-foreground">{cnpjMatriz}</span>
              </Row>
              <Row label="Unidade de negócios:" icon={Boxes}>
                <span className="rounded-full bg-tudo-blue/10 px-2.5 py-0.5 text-xs font-bold uppercase text-tudo-blue">
                  {unidadeNegocios}
                </span>
              </Row>
              <Row label="Tipo:" icon={Tag}>
                <ClearableSelect value={tipo} onChange={setTipo} placeholder="Selecione...">
                  {tipoOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </ClearableSelect>
              </Row>
              <Row label="Sub-Tipo:" icon={Tags}>
                <ClearableSelect value={subTipo} onChange={setSubTipo} placeholder="Selecione...">
                  {subTipoOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </ClearableSelect>
              </Row>
              <Row label="Natureza:" icon={Coins}>
                <ClearableSelect value={natureza} onChange={handleNaturezaChange} placeholder="Selecione...">
                  <SelectItem value="Valor">Valor</SelectItem>
                  <SelectItem value="Produto">Produto</SelectItem>
                </ClearableSelect>
              </Row>
              <Row label={natureza === "Produto" ? "Produto:" : "Valor:"} icon={Coins}>
                <ClearableInput
                  value={naturezaValor}
                  onChange={handleNaturezaInputChange}
                  placeholder={natureza === "Valor" ? "R$ 0,00" : ""}
                  inputMode={natureza === "Valor" ? "decimal" : "text"}
                />
              </Row>
              <Row label="Vigência de:" icon={CalendarDays}>
                <ClearableInput
                  type="date"
                  value={vigenciaDe}
                  onChange={setVigenciaDe}
                />
              </Row>
              <Row label="Vigência até:" icon={CalendarDays}>
                <ClearableInput
                  type="date"
                  value={vigenciaAte}
                  onChange={setVigenciaAte}
                />
              </Row>
            </div>
          </section>

          {/* Contrapartida do Investimento */}
          <section className="rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5">
            <SectionHeader icon={Handshake}>Contrapartida do Investimento</SectionHeader>

            <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
              <Row label="Demanda acordada:" icon={Tag}>
                <ClearableInput
                  value={demandaAcordada}
                  onChange={setDemandaAcordada}
                />
              </Row>
              <Row label="Prazo:" icon={CalendarDays}>
                <ClearableInput
                  value={prazo}
                  onChange={setPrazo}
                />
              </Row>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setDemaisOpen(true)}
                className="group flex items-center gap-2 rounded-lg bg-tudo-green px-4 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:-translate-y-0.5 hover:bg-tudo-green/90 hover:shadow-lg"
              >
                <Plus className="h-4 w-4 transition-transform group-hover:rotate-90" />
                Demais contrapartidas
              </button>
              {demaisContrapartida && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-tudo-green/10 px-3 py-1 text-sm font-semibold text-tudo-green">
                  <Check className="h-3.5 w-3.5" />
                  {demaisContrapartida === "1_unidade_dia" && "1 unidade por dia"}
                  {demaisContrapartida === "7_unidades_semana" && "7 unidades por semana"}
                  {demaisContrapartida === "30_unidades_mes" && "30 unidades por mês"}
                </span>
              )}
            </div>
          </section>

          {/* Ações */}
          <div className="flex gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground transition-all hover:border-tudo-blue hover:text-tudo-blue focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2 sm:flex-none sm:px-8"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-tudo-blue to-tudo-blue/85 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-lg shadow-tudo-blue/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-tudo-blue/30 focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2 sm:flex-none sm:px-10"
            >
              <Save className="h-4 w-4" />
              Salvar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog open={demaisOpen} onOpenChange={handleDemaisOpenChange}>
      <DialogContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-tudo-blue to-tudo-blue/80 px-5 py-4">
          <div className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-white/10 blur-xl" />
          <div className="relative flex items-center justify-between">
            <DialogTitle className="text-left text-sm font-bold uppercase tracking-wide text-white">
              Demais Contrapartidas
            </DialogTitle>
            <button
              onClick={() => handleDemaisOpenChange(false)}
              className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="space-y-4 bg-background p-5">
          <div className="space-y-2">
            {[
              { value: "1_unidade_dia", label: "1 unidade por dia" },
              { value: "7_unidades_semana", label: "7 unidades por semana" },
              { value: "30_unidades_mes", label: "30 unidades por mês" },
            ].map((opt) => {
              const selected = demaisContrapartida === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDemaisContrapartida(opt.value)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3.5 text-left text-sm transition-all hover:-translate-y-0.5 hover:shadow-md ${
                    selected
                      ? "border-tudo-blue bg-tudo-blue/5 font-semibold text-tudo-blue"
                      : "border-border bg-card text-foreground hover:border-tudo-blue"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      selected ? "border-tudo-blue bg-tudo-blue" : "border-border"
                    }`}
                  >
                    {selected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => handleDemaisOpenChange(false)}
              className="rounded-xl bg-gradient-to-r from-tudo-blue to-tudo-blue/85 px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              Confirmar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
